/**
 * ConciergePage — the Concierge as a full page.
 *
 * Uses the EXACT same shell as the chat page (`chat-layout` + `<Sidebar />` +
 * `chat-main-content`) so it looks and feels like a regular chat page. The page
 * owns the session list and the new-chat / history controls (top-right of the
 * header, just like Agent of Agents); the `ConciergeChatPanel` is the
 * controlled conversation surface, remounted per active session via `key`.
 *
 * Reached from the widget's FAB (and routable at `/<tenant>/concierge`). Gated
 * by the `modules.concierge` flag at the route level.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { ConfigProvider, Tooltip, theme as antdTheme } from 'antd';
import type { ThemeConfig } from 'antd';
import { Sidebar } from '@/components/layout/Sidebar';
import { getDarkTheme } from '@/styles/theme';
import { useTenantPath } from '@/routing';
import { useLanguage } from '@/hooks/useLanguage';
import { useFeature } from '@/hooks/useFeature';
import { useAuth } from '@modules/auth';
import { useBrandAssets } from '@/brand';
import { ConciergeChatPanel } from '../components/ConciergeChatPanel';
import { ConciergeHistoryPopover } from '../components/ConciergeHistoryPopover';
import { ConciergePersonaModal } from '../components/ConciergePersonaModal';
import { conciergeApi } from '../services/conciergeApi';
import { GearIcon } from '../../../components/GearIcon';
import type { ConciergeConversation } from '../types';
import './ConciergePage.css';

const PAGE_SIZE = 50;

/** Hide empty front-door sessions (no messages) from the history list. */
const nonEmpty = (s: ConciergeConversation) => (s.message_count ?? 0) > 0;

export function ConciergePage() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const tenantPath = useTenantPath();
  const assets = useBrandAssets() as { icon?: string; logo?: string; conciergeIcon?: string };
  const icon = assets?.conciergeIcon || assets?.icon || assets?.logo;

  // ---- Meta-agent space theming ------------------------------------------
  // The Concierge is the platform's meta-agent front door, so its page gets a
  // distinct, always-deep brand treatment regardless of the app light/dark
  // toggle. Mechanism: a nested AntD ConfigProvider (same precedent as
  // InsightsDashboardPage) composed from the brand dark theme, so every AntD
  // token still derives from the active tenant brand. The CSS side lives in
  // ConciergePage.css under `.concierge-page--meta`, which re-scopes the app
  // CSS variables for everything inside <main> — the Sidebar sits outside and
  // keeps the regular app theme.
  const metaTheme = useMemo<ThemeConfig>(() => ({
    ...getDarkTheme(),
    algorithm: antdTheme.darkAlgorithm,
  }), []);

  // Popups (history popover, tooltips, chat-input dropdowns) portal INTO the
  // themed main area instead of document.body, so they inherit the meta
  // space's re-scoped CSS variables and stay legible in light app mode.
  // They mount inside `.concierge-popup-host` — an absolutely-positioned,
  // zero-impact anchor — NOT directly into <main>: main is a flex column, and
  // an in-flow popup there steals real height from the chat (the input bar
  // visibly jumped up ~230px whenever the history popover opened).
  const popupHostRef = useRef<HTMLDivElement | null>(null);
  const getPopupContainer = useCallback(
    (): HTMLElement => popupHostRef.current ?? document.body,
    [],
  );

  // Persona editing is a tenant-wide admin action, so the entry point needs
  // BOTH conditions the backend route enforces: the right role
  // (group_admin / super_admin) AND the tenant's own `edit_prompt` flag.
  // Role alone showed the gear to every group admin, including on tenants
  // where the feature is off — they could open an editor that was
  // permanently read-only, which reads as a broken screen rather than an
  // absent feature. (Writes were never at risk: the modal takes `can_edit`
  // from the server and PUT /config is gated by the same flag.)
  const { user } = useAuth();
  const canEditPersona =
    useFeature('modules.concierge.edit_prompt') &&
    ['group_admin', 'super_admin'].includes(user?.role ?? '');
  const [personaOpen, setPersonaOpen] = useState(false);
  const [sessions, setSessions] = useState<ConciergeConversation[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  // Per-tenant Concierge model config (returned on create) — stable across
  // every session, so we capture it once and hand it to the panel.
  const [allowedModels, setAllowedModels] = useState<string | string[]>('all');
  const [defaultModel, setDefaultModel] = useState<string>('');
  // `offsetRef` tracks RAW server rows fetched (for correct pagination); the
  // displayed list hides empty front-door sessions (message_count 0) so past
  // visits that never sent a message don't clutter the history.
  const offsetRef = useRef(0);

  const refreshSessions = useCallback(async () => {
    try {
      const res = await conciergeApi.listConversations({ limit: PAGE_SIZE, offset: 0 });
      offsetRef.current = res.conversations.length;
      setSessions(res.conversations.filter(nonEmpty));
      setHasMore(res.has_more);
    } catch {
      /* non-fatal — history just stays as-is */
    }
  }, []);

  const loadMoreSessions = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const res = await conciergeApi.listConversations({ limit: PAGE_SIZE, offset: offsetRef.current });
      offsetRef.current += res.conversations.length;
      setSessions(prev => {
        const seen = new Set(prev.map(s => s.conversation_id));
        return [...prev, ...res.conversations.filter(s => nonEmpty(s) && !seen.has(s.conversation_id))];
      });
      setHasMore(res.has_more);
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasMore]);

  // The organization's Concierge model slot, from a list, a create or a save
  // in the settings — whichever is newest is what the next conversation uses.
  const applyModelConfig = useCallback((mc: { default_model: string | null; allowed_models: string[] }) => {
    setAllowedModels(mc.allowed_models && mc.allowed_models.length ? mc.allowed_models : 'all');
    setDefaultModel(mc.default_model || '');
  }, []);

  // Start a fresh session and capture the tenant's model config.
  const startSession = useCallback(async () => {
    const conv = await conciergeApi.createConversation();
    if (conv.model_config) applyModelConfig(conv.model_config);
    setSessions(prev => [conv, ...prev.filter(s => s.conversation_id !== conv.conversation_id)]);
    setActiveId(conv.conversation_id);
    return conv;
  }, [applyModelConfig]);

  // On mount: load history and the tenant's model config. Deliberately does NOT
  // create a conversation.
  //
  // It used to. That had three costs: the typing bar stayed disabled until the
  // POST returned, so there was a visible wait before you could type; React 18
  // runs mount effects twice in dev, so two rows were created and the UI
  // sometimes landed on the empty one — which reads as the conversation
  // "restarting" and silently drops the first message; and every visit that
  // never sent anything left an orphan row behind (244 of 406 on this dev DB).
  // The row is now created on the first send instead — see onEnsureConversation.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await conciergeApi.listConversations({ limit: PAGE_SIZE, offset: 0 });
        if (!cancelled) {
          offsetRef.current = res.conversations.length;
          setSessions(res.conversations.filter(nonEmpty));
          setHasMore(res.has_more);
          if (res.model_config) applyModelConfig(res.model_config);
        }
      } catch {
        /* ignore */
      } finally {
        if (!cancelled) setLoadingSessions(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Create the conversation on demand, on the first send. Guarded by a ref so
  // two rapid sends can't race into two rows.
  const ensuringRef = useRef<Promise<string | null> | null>(null);
  const ensureConversation = useCallback(async (): Promise<string | null> => {
    if (activeId) return activeId;
    if (!ensuringRef.current) {
      ensuringRef.current = (async () => {
        try {
          const conv = await startSession();
          return conv.conversation_id;
        } catch {
          return null;
        } finally {
          ensuringRef.current = null;
        }
      })();
    }
    return ensuringRef.current;
  }, [activeId, startSession]);

  // "+" clears to a blank panel rather than creating a row. Same reasoning as
  // mount: the conversation appears when there is something to put in it, so
  // this is instant and there is no in-flight state to guard.
  const onNewSession = useCallback(() => setActiveId(null), []);

  return (
    <div className="chat-layout">
      <Sidebar
        options={{
          showHome: true,
          showChat: false,
          showBots: true,
          showSearch: true,
          showAdmin: true,
          showGroupAdmin: true,
          showSuperAdmin: true,
          showConversations: true,
          showBotsList: true,
          activeItem: 'home',
        }}
      />

      <main className="chat-main-content concierge-page-main concierge-page--meta">
        <ConfigProvider theme={metaTheme} getPopupContainer={getPopupContainer}>
        {/* Out-of-flow anchor for every popup on this page (see popupHostRef). */}
        <div ref={popupHostRef} className="concierge-popup-host" />
        <header className="concierge-page-header">
          <button
            type="button"
            className="concierge-page-back"
            onClick={() => navigate(tenantPath('/'))}
            aria-label={t('concierge.backHome') || t('common.back') || 'Back'}
            title={t('concierge.backHome') || t('common.back') || 'Back'}
          >
            <ArrowLeftOutlined className="rtl-flip" />
          </button>
          {icon ? <img src={icon} alt="" className="concierge-page-logo" /> : null}
          {/* Slim header: title only. The tagline (concierge.greetingSub) is
              owned by the empty-state greeting in the panel — showing it here
              too duplicated the same string twice on screen. */}
          <div className="concierge-page-heading">
            <h1 className="concierge-page-title">{t('concierge.title') || 'Concierge'}</h1>
          </div>

          {/* Top-right corner: history + new conversation, like Agent of Agents. */}
          <div className="concierge-page-actions">
            <ConciergeHistoryPopover
              sessions={sessions}
              activeId={activeId}
              onSelect={setActiveId}
              loading={loadingSessions}
              hasMore={hasMore}
              loadingMore={loadingMore}
              onLoadMore={loadMoreSessions}
            />
            {canEditPersona && (
              <Tooltip
                title={t('concierge.persona.title') || 'Concierge persona'}
                placement="bottom"
                overlayClassName="aoa-tooltip"
              >
                <button
                  type="button"
                  className="aoa-header-icon-btn"
                  onClick={() => setPersonaOpen(true)}
                  aria-label={t('concierge.persona.title') || 'Concierge persona'}
                  data-testid="concierge-persona-open"
                >
                  <GearIcon size={14} />
                </button>
              </Tooltip>
            )}
            <Tooltip
              title={t('concierge.newConversation') || 'New conversation'}
              placement="bottom"
              overlayClassName="aoa-tooltip"
            >
              <button
                type="button"
                className="aoa-header-icon-btn"
                onClick={onNewSession}
                aria-label={t('concierge.newConversation') || 'New conversation'}
                data-testid="concierge-new-session"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 5v14M5 12h14" />
                </svg>
              </button>
            </Tooltip>
          </div>
        </header>

        {/* Controlled conversation surface. NOTE: deliberately NOT keyed by
            activeId. The conversation is created async, so activeId goes
            null → real-id shortly after mount; a key tied to it would remount
            the whole panel (and ChatInput) at that moment and wipe whatever the
            user already started typing. The panel instead reacts to
            `conversationId` via an effect that reloads the timeline, so it
            switches sessions WITHOUT remounting — the draft in the typing bar
            survives. */}
        <ConciergeChatPanel
          conversationId={activeId}
          allowedModels={allowedModels}
          defaultModel={defaultModel}
          onAfterTurn={refreshSessions}
          onEnsureConversation={ensureConversation}
        />
        {canEditPersona && (
          <ConciergePersonaModal
            open={personaOpen}
            onClose={() => setPersonaOpen(false)}
            onModelSaved={applyModelConfig}
          />
        )}
        </ConfigProvider>
      </main>
    </div>
  );
}

export default ConciergePage;
